import { useEffect, useState, lazy, Suspense } from 'react'
import { Globe, Map as MapIcon, Maximize, Minimize, ArrowLeft, AlertTriangle, Play } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'

// Lazy so the heavy globe.gl + three.js bundles don't block the SideNav
// switching to this route — and so 2D users never load three.js.
const CyberGlobe   = lazy(() => import('../components/CyberGlobe'))
const CyberMap2D   = lazy(() => import('../components/CyberMap2D'))
const LobbyOverlay = lazy(() => import('../components/LobbyOverlay'))

/** Detect mobile / low-power devices where WebGL globe.gl regularly OOMs.
 * Auto-default to 2D for them so /app/threat-globe doesn't crash the tab. */
function isMobileOrLowPower(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod|Android|Mobile/i.test(ua)) return true
  // Browsers report device memory in GB; treat <4 as low-power
  const mem = (navigator as any).deviceMemory
  if (typeof mem === 'number' && mem < 4) return true
  return false
}

interface ThreatGlobePageProps {
  /** Override page title — used by /app/war-room-globe. */
  title?: string
  /** Persists view-mode in a separate URL key so the two routes don't fight. */
  modeKey?: string
  /** Lobby / display-only mode: hides chrome, shows headline + ticker overlay,
   *  prompts for fullscreen, kills tooltips. Designed for a TV across the room. */
  lobby?: boolean
}

/**
 * Standalone full-viewport threat view — 2D Leaflet map OR 3D globe.gl globe.
 * Toggle is URL-bound (?mode=2d|3d) so links restore the choice. Mobile devices
 * default to 2D because three.js + globe.gl regularly OOMs in mobile Safari.
 */
export default function ThreatGlobePage({ title = 'Threat Globe', modeKey = 'mode', lobby = false }: ThreatGlobePageProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlMode = (searchParams.get(modeKey) || '').toLowerCase()
  const initialMode = urlMode === '2d' ? '2d'
                    : urlMode === '3d' ? '3d'
                    : (isMobileOrLowPower() ? '2d' : '3d')
  const [mode, setMode] = useState<'2d' | '3d'>(initialMode)
  const [height, setHeight] = useState<number>(typeof window !== 'undefined' ? window.innerHeight - 120 : 800)
  const [isFull, setIsFull] = useState(false)
  const [globeError, setGlobeError] = useState<string | null>(null)

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (mode === (isMobileOrLowPower() ? '2d' : '3d')) next.delete(modeKey)
    else next.set(modeKey, mode)
    setSearchParams(next, { replace: true })
  }, [mode, modeKey, setSearchParams, searchParams])

  useEffect(() => {
    const resize = () => setHeight(window.innerHeight - (isFull ? 40 : 120))
    resize()
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [isFull])

  // Fullscreen API + auto-recover on globe.gl WebGL crash
  useEffect(() => {
    const el = document.documentElement
    const toggle = async () => {
      try {
        if (!document.fullscreenElement) { await el.requestFullscreen(); setIsFull(true) }
        else                              { await document.exitFullscreen(); setIsFull(false) }
      } catch {}
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'f' && !e.metaKey && !e.ctrlKey) { e.preventDefault(); toggle() }
    }
    const onChange = () => setIsFull(!!document.fullscreenElement)
    // Catch unhandled WebGL / three.js errors so the page can offer a fallback
    const onWindowError = (event: ErrorEvent) => {
      const msg = (event.message || '').toLowerCase()
      if (msg.includes('webgl') || msg.includes('out of memory') || msg.includes('three')) {
        setGlobeError(event.message)
        setMode('2d')
      }
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('error', onWindowError)
    document.addEventListener('fullscreenchange', onChange)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('error', onWindowError)
      document.removeEventListener('fullscreenchange', onChange)
    }
  }, [])

  const goFull = async () => {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen()
      else                              await document.exitFullscreen()
    } catch {}
  }

  const Icon = mode === '3d' ? Globe : MapIcon
  const subhead = mode === '3d'
    ? 'Full-screen war-room view · MITRE + MISP catalog (969 actors) · live attack arcs · subsea cables · KEV rings'
    : 'Flat projection · single world · live IOC feed · subsea cables · KEV targets · MITRE actor markers'

  // ── Lobby/display mode: page-windowed by default, opt-in fullscreen via button ──
  if (lobby) {
    // FULLSCREEN BRANCH: the browser fullscreen mode still includes the page
    // Shell (sidebar + topnav). Without a `fixed inset-0` escape, the lobby
    // container sits *inside* that chrome and the bottom ticker gets clipped
    // by the topnav offset. So when isFull: we render fixed full-viewport.
    if (isFull) {
      return (
        <div className="fixed inset-0 z-[1000] bg-black overflow-hidden">
          {/* Discreet exit toolbar (top-right, low opacity until hover) */}
          <div className="absolute top-2 right-2 z-[600] flex items-center gap-2 opacity-30 hover:opacity-100 transition-opacity">
            <div className="flex items-center rounded-md border border-border overflow-hidden text-[10px] bg-surface-card">
              <button type="button" onClick={() => setMode('2d')}
                className={`px-2 py-1 ${mode === '2d' ? 'bg-blue/20 text-blue-light' : 'text-text-muted'}`}>2D</button>
              <button type="button" onClick={() => setMode('3d')}
                className={`px-2 py-1 ${mode === '3d' ? 'bg-blue/20 text-blue-light' : 'text-text-muted'}`}>3D</button>
            </div>
            <button type="button" onClick={goFull}
              className="btn-secondary text-xs inline-flex items-center gap-1.5">
              <Minimize size={12} /> Exit fullscreen
            </button>
          </div>

          {/* Globe fills the viewport */}
          <Suspense fallback={<div className="flex items-center justify-center h-full text-text-muted text-sm">Loading {mode === '3d' ? 'globe' : 'map'}…</div>}>
            <div className="absolute inset-0">
              {mode === '3d'
                ? <CyberGlobe height={typeof window !== 'undefined' ? window.innerHeight : 800} hideChrome />
                : <CyberMap2D height={typeof window !== 'undefined' ? window.innerHeight : 800} hideChrome />}
            </div>
          </Suspense>

          {/* Stats + ticker — absolute inside this fixed container so they
              anchor to the viewport edges */}
          <Suspense fallback={null}>
            <LobbyOverlay />
          </Suspense>
        </div>
      )
    }

    // WINDOWED BRANCH: standard page layout with toolbar + card-sized globe area
    const containerHeight = typeof window !== 'undefined' ? Math.max(560, window.innerHeight - 180) : 700

    return (
      <div className="space-y-3 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Link to="/app" className="text-text-muted hover:text-text-primary text-[11px] flex items-center gap-1">
              <ArrowLeft size={12} /> Overview
            </Link>
            <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
              <Globe size={24} className="text-rose" /> {title}
            </h1>
            <span className="text-[11px] text-text-muted">lobby / display mode</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-md border border-border overflow-hidden text-[10px]">
              <button type="button" onClick={() => setMode('2d')}
                className={`px-2 py-1 ${mode === '2d' ? 'bg-blue/20 text-blue-light font-semibold' : 'text-text-muted hover:bg-surface-hover'}`}>2D</button>
              <button type="button" onClick={() => setMode('3d')}
                className={`px-2 py-1 ${mode === '3d' ? 'bg-blue/20 text-blue-light font-semibold' : 'text-text-muted hover:bg-surface-hover'}`}>3D</button>
            </div>
            <button type="button" onClick={goFull}
              className="btn-secondary text-xs inline-flex items-center gap-1.5"
              title="Press F">
              <Maximize size={12} /> Fullscreen
            </button>
          </div>
        </div>

        <div className="relative card p-0 overflow-hidden bg-black" style={{ height: containerHeight }}>
          <Suspense fallback={<div className="flex items-center justify-center h-full text-text-muted text-sm">Loading {mode === '3d' ? 'globe' : 'map'}…</div>}>
            {mode === '3d'
              ? <CyberGlobe height={containerHeight} hideChrome />
              : <CyberMap2D height={containerHeight} hideChrome />}
          </Suspense>
          <Suspense fallback={null}>
            <LobbyOverlay />
          </Suspense>
        </div>

        <div className="text-[10px] text-text-muted text-center">
          Press <kbd className="font-mono px-1 rounded border border-border">F</kbd> for true fullscreen ·
          {mode === '3d' ? ' drag to rotate · auto-rotation enabled' : ' drag to pan · click markers for details'}
        </div>
      </div>
    )
  }

  // ── Standard analyst mode (existing layout) ──
  return (
    <div className="space-y-3 animate-fade-in">
      {!isFull && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Link to="/app/cyber" className="text-text-muted hover:text-text-primary text-[11px] flex items-center gap-1">
              <ArrowLeft size={12} /> Cyber
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
                <Icon size={24} className="text-rose" /> {title}
              </h1>
              <p className="text-[11px] text-text-secondary mt-0.5">{subhead}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* 2D / 3D toggle */}
            <div className="flex items-center rounded-md border border-border overflow-hidden text-[10px]">
              <button type="button" onClick={() => setMode('2d')}
                className={`px-2 py-1 ${mode === '2d' ? 'bg-blue/20 text-blue-light font-semibold' : 'text-text-muted hover:bg-surface-hover'}`}>
                2D
              </button>
              <button type="button" onClick={() => setMode('3d')}
                className={`px-2 py-1 ${mode === '3d' ? 'bg-blue/20 text-blue-light font-semibold' : 'text-text-muted hover:bg-surface-hover'}`}>
                3D
              </button>
            </div>
            <button type="button" onClick={goFull}
              className="btn-secondary text-xs inline-flex items-center gap-1.5"
              title="Press F">
              <Maximize size={12} /> Fullscreen
            </button>
          </div>
        </div>
      )}

      {isFull && (
        <div className="fixed top-2 right-2 z-50 flex items-center gap-2">
          <div className="flex items-center rounded-md border border-border overflow-hidden text-[10px] bg-surface-card">
            <button type="button" onClick={() => setMode('2d')}
              className={`px-2 py-1 ${mode === '2d' ? 'bg-blue/20 text-blue-light' : 'text-text-muted'}`}>2D</button>
            <button type="button" onClick={() => setMode('3d')}
              className={`px-2 py-1 ${mode === '3d' ? 'bg-blue/20 text-blue-light' : 'text-text-muted'}`}>3D</button>
          </div>
          <button type="button" onClick={goFull}
            className="btn-secondary text-xs inline-flex items-center gap-1.5">
            <Minimize size={12} /> Exit fullscreen
          </button>
        </div>
      )}

      {globeError && mode === '2d' && (
        <div className="card p-3 border border-amber/40 bg-amber/5 text-[11px] text-amber-light flex items-center gap-2">
          <AlertTriangle size={12} />
          3D globe failed to load (WebGL crash on this device) — switched to 2D.
        </div>
      )}

      <Suspense fallback={<div className="card flex items-center justify-center" style={{ height }}><span className="text-text-muted text-sm">Loading {mode === '3d' ? 'globe' : 'map'}…</span></div>}>
        <div className="card p-0 overflow-hidden">
          {mode === '3d' ? <CyberGlobe height={height} /> : <CyberMap2D height={height} />}
        </div>
      </Suspense>

      {!isFull && (
        <div className="text-[10px] text-text-muted text-center">
          Press <kbd className="font-mono px-1 rounded border border-border">F</kbd> for fullscreen ·
          {mode === '3d' ? ' drag to rotate · scroll to zoom · auto-rotation enabled' : ' drag to pan · scroll to zoom · click markers for details'}
        </div>
      )}
    </div>
  )
}
